<template>
    <ActivationConfirmationModal
        :entities="entities"
        :saving="saving"
        :show-pause-option="hasPauseFeature"
        @confirm="onConfirm"
    />
</template>

<script lang="ts">
    import { Component, Prop, Vue } from '@evidentid/vue-property-decorator';

    import { useUserStore } from '@evidentid/dashboard-commons/modules/auth/store/user';
    import {
        hasSufficientFeatures,
    } from '@evidentid/dashboard-commons/modules/role-base-access-control/utils/has-sufficient-features/hasSuffiencientFeatures';
    import { EntityPatch, PatchOperationType } from '@evidentid/rpweb-api-client';
    import { Entity } from '@evidentid/tprm-portal-lib/models/dashboard';
    import { RbacFeature } from '@evidentid/tprm-portal-lib/models/rbac';

    import ActivationConfirmationModal
        from '@/modules/entity-management/components/ActivationConfirmationModal/ActivationConfirmationModal.vue';
    import { useEntityManagementStore } from '@/modules/entity-management/store';

    // Declares every event this procedure component `$emit`s -- without a declared `emits`,
    // Vue 3 warns "Component emitted event 'abort'/'finish' but it is neither declared in
    // the emits option nor as an 'onAbort'/'onFinish' prop" every time it fires.
    @Component({
        components: { ActivationConfirmationModal },
        emits: [ 'abort', 'finish' ],
    })

    export default class ActivateEntityProcedure extends Vue {
        // `uuid` is unused; declared purely so Vue 3 doesn't warn "Extraneous non-props
        // attributes (uuid)" (see DeactivateEntityProcedure.vue).
        @Prop({ type: String, default: '' })
        private uuid?: string;

        @Prop({ type: String, default: '' })
        private rpName!: string;

        @Prop({ type: Array, default: () => [] })
        private entities!: Entity[];

        @Prop({ type: Boolean, default: false })
        private isBulk!: boolean;

        private saving: boolean = false;

        // Pause state is gated behind its own feature: a role that can deactivate/reactivate
        // an entity but was never granted pause access shouldn't be able to change it as a
        // side effect of activation, and would have no Pause/Unpause item to undo it with.
        private get hasPauseFeature(): boolean {
            return hasSufficientFeatures(
                [ RbacFeature.dashboardPauseEntity ],
                useUserStore().currentRpRbacFeatures || {},
            );
        }

        private onConfirm({ confirmed, keepPaused }: { confirmed: boolean, keepPaused: boolean }) {
            confirmed ? this.activateInsured(keepPaused) : this.close();
        }

        private close(): void {
            this.$emit('abort');
        }

        private async activateInsured(keepPaused: boolean) {
            const { rpName, entities, isBulk, hasPauseFeature } = this;
            // A selection that filters down to nothing has no patch to send; closing is the
            // honest outcome rather than a request with an empty body.
            if (entities.length === 0) {
                this.close();
                return;
            }
            this.saving = true;
            await useEntityManagementStore().patchEntities({
                rpName,
                isBulk,
                patch: entities.map((entity) => {
                    const patch: EntityPatch = {
                        id: entity.id,
                        active: {
                            op: PatchOperationType.replace,
                            newValue: true,
                        },
                    };
                    // Unchecking the box must actively unpause an entity that was already
                    // paused when it was deactivated, not merely leave the field alone --
                    // but only for a user who can actually change pause state; otherwise the
                    // entity's prior pause state is left untouched.
                    if (hasPauseFeature) {
                        patch.paused = {
                            op: PatchOperationType.replace,
                            newValue: keepPaused,
                        };
                    }
                    return patch;
                }),
            });
            this.$emit('finish', this.entities);
        }
    }
</script>
